import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { getCustomers, getResellerStats, getCustomerById } from './connectors';
import { Customer, ResellerStats } from './connectors';

// Define path type to avoid repetition
export interface SidebarPath {
  name: string;
  key: string;
  slug: string;
  icon: null;
  disabled: boolean;
  beta: boolean;
}

// Define sidebar type
export interface Sidebar {
  name: string;
  uuid: string;
  provider: string;
  paths: SidebarPath[];
}

// Common sidebar paths
export const resellerSidebarPaths: SidebarPath[] = [
  {
    name: 'Dashboard',
    key: 'dashboard',
    slug: '/resellers',
    icon: null,
    disabled: false,
    beta: false,
  },
  {
    name: 'Customers',
    key: 'customers',
    slug: '/resellers/customers',
    icon: null,
    disabled: false,
    beta: false,
  },
  {
    name: 'Products',
    key: 'products',
    slug: '/resellers/products',
    icon: null,
    disabled: false,
    beta: false,
  },
  {
    name: 'Billing',
    key: 'billing',
    slug: '/resellers/billing',
    icon: null,
    disabled: false,
    beta: false,
  },
  {
    name: 'Reports',
    key: 'reports',
    slug: '/resellers/reports',
    icon: null,
    disabled: false,
    beta: false,
  }
];

// Create reusable sidebar object
export const getResellerSidebar = (): Sidebar => ({
  name: '@Reseller Portal',
  uuid: '',
  provider: '',
  paths: resellerSidebarPaths
});

// Unified context type that includes both reseller and customer functionality
interface ResellerContextType {
  // Sidebar for navigation
  sidebar: Sidebar;
  
  // Reseller customers list data
  customers: Customer[];
  stats: ResellerStats | null;
  loading: {
    customers: boolean;
    stats: boolean;
    selectedCustomer: boolean;
  };
  error: {
    customers: string | null;
    stats: string | null;
    selectedCustomer: string | null;
  };
  
  // Customer detail data
  selectedCustomer: Customer | null;
  customerId: string | null;
  
  // Actions
  refreshCustomers: () => Promise<void>;
  refreshStats: () => Promise<void>;
  setCustomerId: (id: string | null) => void;
  refreshCustomer: () => Promise<void>;
}

// Create default context with no-op functions and empty values
 
const ResellerContext = createContext<ResellerContextType>({
  sidebar: getResellerSidebar(),
  customers: [],
  stats: null,
  loading: {
    customers: false,
    stats: false,
    selectedCustomer: false,
  },
  error: {
    customers: null,
    stats: null,
    selectedCustomer: null,
  },
  selectedCustomer: null,
  customerId: null,
  refreshCustomers: async () => {},
  refreshStats: async () => {},
  setCustomerId: () => {},
  refreshCustomer: async () => {},
});

interface ResellerProviderProps {
  children: ReactNode;
  initialCustomerId?: string | null;
}

 
export const ResellerProvider: React.FC<ResellerProviderProps> = ({ 
  children,
  initialCustomerId = null
}) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [stats, setStats] = useState<ResellerStats | null>(null);
  const [loading, setLoading] = useState({
    customers: false,
    stats: false,
    selectedCustomer: false
  });
  const [error, setError] = useState<{
    customers: string | null;
    stats: string | null;
    selectedCustomer: string | null;
  }>({
    customers: null,
    stats: null,
    selectedCustomer: null
  });
  
  // Customer details state
  const [customerId, setCustomerId] = useState<string | null>(initialCustomerId);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  const sidebar = getResellerSidebar();

  // Refresh all customers list
  const refreshCustomers = async () => {
    try {
      setLoading(prev => ({ ...prev, customers: true }));
      setError(prev => ({ ...prev, customers: null }));
      const data = await getCustomers();
      setCustomers(data);
    } catch (err) {
      setError(prev => ({ ...prev, customers: 'Failed to load customers' }));
    } finally {
      setLoading(prev => ({ ...prev, customers: false }));
    }
  };

  // Refresh reseller stats
  const refreshStats = async () => {
    try {
      setLoading(prev => ({ ...prev, stats: true }));
      setError(prev => ({ ...prev, stats: null }));
      const data = await getResellerStats();
      setStats(data);
    } catch (err) {
      setError(prev => ({ ...prev, stats: 'Failed to load stats' }));
    } finally {
      setLoading(prev => ({ ...prev, stats: false }));
    }
  };
  
  // Refresh selected customer details
  const refreshCustomer = async () => {
    if (!customerId) {
      setSelectedCustomer(null);
      return;
    }
    
    try {
      setLoading(prev => ({ ...prev, selectedCustomer: true }));
      setError(prev => ({ ...prev, selectedCustomer: null }));
      const customer = await getCustomerById(customerId);
      setSelectedCustomer(customer);
    } catch (err) {
      setError(prev => ({ ...prev, selectedCustomer: 'Failed to load customer details' }));
      console.error(`Error loading customer with ID ${customerId}:`, err);
    } finally {
      setLoading(prev => ({ ...prev, selectedCustomer: false }));
    }
  };

  // Load all customers and stats on mount
  useEffect(() => {
    refreshCustomers();
    refreshStats();
  }, []);
  
  // Load selected customer when ID changes
  useEffect(() => {
    refreshCustomer();
  }, [customerId]);

  const contextValue = { 
    sidebar, 
    customers, 
    stats, 
    loading, 
    error, 
    selectedCustomer,
    customerId,
    refreshCustomers, 
    refreshStats,
    setCustomerId,
    refreshCustomer
  };

  return (
    <ResellerContext.Provider value={contextValue}>
      {children}
    </ResellerContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useResellerContext = (): ResellerContextType => {
  return useContext(ResellerContext);
}; 